import axios from 'axios'
import {
  PcgReport,
  PcgReportStatus,
  PcgReportSummary,
  VariationLineItem,
} from '../models'
import { encodeBase64HtmlPayloadField } from '../../../shared/utils/base64HtmlPayload'
import { mockPcgReport, mockPcgReportSummaries, mockPcgReportStatuses } from './mock-data'
export {
  startPcgReportGenerationJob,
  getPcgReportGenerationJob,
  getActivePcgReportGenerationJob,
  downloadPcgReportGenerationJobFile,
} from './report-jobs'

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/PcgReports` : undefined
export const pcgReportsApiBaseUrl = API_URL

const getApiUrl = () => {
  if (!API_URL) {
    throw new Error('REACT_APP_API_URL is not configured.')
  }
  return API_URL
}

const delay = (ms = 400) => new Promise((resolve) => setTimeout(resolve, ms))

const encodePcgReportHtmlPayload = (payload: PcgReport): PcgReport => ({
  ...payload,
  reportBuilderSections: payload.reportBuilderSections?.map((section) => ({
    ...section,
    contentHtml: encodeBase64HtmlPayloadField(section.contentHtml),
  })),
})

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const matchesSearchText = (value: string | number | null | undefined, search: string) => {
  const source = String(value ?? '').trim()
  const term = search.trim()
  if (!source || !term) return false
  if (!term.includes('*')) return source.toLowerCase().includes(term.toLowerCase())

  return new RegExp(escapeRegExp(term).replace(/\\\*/g, '.*'), 'i').test(source)
}

export type PcgReportImageUploadResponse = {
  fileName: string
  url: string
  contentType: string
  size: number
}

export type SignatureSlot = 'preparedBy' | 'approvedBy'
export type PcgUserLookupItem = {
    id: string;
    displayName:
    string; email:
    string;
    roles: string[]
}
export type RoleLookupItem = {
    id: string;
    name?: string;
    displayId?: number
}
export type UserSignatureResponse = {
    userId: string;
    fileName: string;
    contentType?: string;
    size?: number
}

export const listPcgReports = async (options?: {
  projectId?: string | number
  status?: PcgReportStatus | 'all'
  search?: string
  skip?: number
  take?: number
  sort?: string
  filter?: string
  includeItems?: boolean
}): Promise<{ items: PcgReportSummary[]; totalCount: number; pageNumber: number; totalPages: number }> => {
  if (API_URL) {
    try {
      const params: Record<string, string> = {}
      if (options?.projectId) params.projectId = options.projectId.toString()
      if (options?.status && options.status !== 'all') params.status = options.status
      if (options?.search) params.search = options.search
      if (options?.includeItems) params.includeItems = 'true'
      if (options?.skip !== undefined) params.skip = options.skip.toString()
      if (options?.take !== undefined) params.take = options.take.toString()
      if (options?.sort) params.sort = options.sort
      if (options?.filter) params.filter = options.filter

      const { data } = await axios.get(API_URL, { params })
      const items: PcgReportSummary[] = data.items ?? data.Items ?? data ?? []
      const totalCount = data.totalCount ?? data.TotalCount ?? items.length
      const pageNumber = data.pageNumber ?? data.PageNumber ?? 1
      const totalPages = data.totalPages ?? data.TotalPages ?? 1
      return { items, totalCount, pageNumber, totalPages }
    } catch (error) {
      console.warn('Falling back to mock PCG reports.', error)
    }
  }

  const normalizedStatus = options?.status && options.status !== 'all' ? options.status.toLowerCase() : undefined
  const term = options?.search?.trim() ?? ''
  const filtered = mockPcgReportSummaries.filter((report) => {
    const matchesStatus = !normalizedStatus || (report.status ?? 'draft') === normalizedStatus
    const matchesSearch =
      !term ||
      matchesSearchText(report.reportNumber, term) ||
      matchesSearchText(report.dateOfIssue, term)
    return matchesStatus && matchesSearch
  })

  const parseDate = (value?: string | null) => {
    if (!value) return 0
    const parsed = new Date(value).getTime()
    return Number.isNaN(parsed) ? 0 : parsed
  }

  const compareStrings = (a?: string | null, b?: string | null) =>
    (a ?? '').toLowerCase().localeCompare((b ?? '').toLowerCase())

  const revisionSuffixRegex = /-rev-(\d+)\s*$/i

  const stripRevisionSuffix = (value?: string | null) => {
    if (!value) return ''
    return value.trim().replace(revisionSuffixRegex, '').trim()
  }

  const extractRevisionNumber = (value?: string | null) => {
    if (!value) return 0
    const match = value.trim().match(revisionSuffixRegex)
    if (!match?.[1]) return 0
    const parsed = Number(match[1])
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 0
  }

  const normalizeNumericToken = (value: string) => {
    const normalized = value.replace(/^0+/, '')
    return normalized.length > 0 ? normalized : '0'
  }

  const extractPcgNumberToken = (value?: string | null) => {
    if (!value) return undefined
    const trimmed = value.trim()
    if (!trimmed) return undefined

    const trailingMatch = trimmed.match(/(?:pcg\s*)?(\d+)\s*$/i)
    if (trailingMatch?.[1]) {
      return normalizeNumericToken(trailingMatch[1])
    }

    const numericMatches = trimmed.match(/\d+/g)
    if (!numericMatches || numericMatches.length === 0) {
      return undefined
    }

    return normalizeNumericToken(numericMatches[numericMatches.length - 1])
  }

  const compareNumericTokens = (left: string, right: string) => {
    if (left.length !== right.length) {
      return left.length - right.length
    }
    return left.localeCompare(right)
  }

  const compareReportNumbers = (a?: string | null, b?: string | null) => {
    const leftBase = stripRevisionSuffix(a)
    const rightBase = stripRevisionSuffix(b)
    const leftToken = extractPcgNumberToken(leftBase)
    const rightToken = extractPcgNumberToken(rightBase)

    if (leftToken && rightToken) {
      const numericComparison = compareNumericTokens(leftToken, rightToken)
      if (numericComparison !== 0) {
        return numericComparison
      }
    } else if (leftToken) {
      return -1
    } else if (rightToken) {
      return 1
    }

    const baseTextComparison = compareStrings(leftBase, rightBase)
    if (baseTextComparison !== 0) {
      return baseTextComparison
    }

    const revisionComparison = extractRevisionNumber(a) - extractRevisionNumber(b)
    if (revisionComparison !== 0) {
      return revisionComparison
    }

    return compareStrings(a, b)
  }

  const compareDates = (a?: string | null, b?: string | null) => parseDate(a) - parseDate(b)

  const getCreatedAt = (report: PcgReportSummary) => report.createdAt ?? report.dateOfIssue ?? report.updatedAt
  const getUpdatedAt = (report: PcgReportSummary) => report.updatedAt ?? report.createdAt ?? report.dateOfIssue

  const sortParam = options?.sort?.trim()
  const sorted = [...filtered]
  if (sortParam) {
    const [field, direction] = sortParam.split(/\s+/)
    const dir = direction?.toLowerCase() === 'desc' ? -1 : 1
    switch (field?.toLowerCase()) {
      case 'reportnumber':
        sorted.sort((a, b) => compareReportNumbers(a.reportNumber, b.reportNumber) * dir)
        break
      case 'dateofissue':
        sorted.sort((a, b) => {
          const diff = compareDates(a.dateOfIssue, b.dateOfIssue)
          return diff !== 0 ? diff * dir : compareReportNumbers(a.reportNumber, b.reportNumber) * dir
        })
        break
      case 'status':
        sorted.sort((a, b) => compareStrings(a.status, b.status) * dir)
        break
      case 'created':
      case 'createdat':
        sorted.sort((a, b) => {
          const diff = compareDates(getCreatedAt(a), getCreatedAt(b))
          return diff !== 0 ? diff * dir : compareReportNumbers(a.reportNumber, b.reportNumber) * dir
        })
        break
      case 'updatedat':
        sorted.sort((a, b) => {
          const diff = compareDates(getUpdatedAt(a), getUpdatedAt(b))
          return diff !== 0 ? diff * dir : compareReportNumbers(a.reportNumber, b.reportNumber) * dir
        })
        break
      default:
        sorted.sort((a, b) => {
          const diff = compareDates(getCreatedAt(a), getCreatedAt(b))
          return diff !== 0 ? diff * dir : compareReportNumbers(a.reportNumber, b.reportNumber) * dir
        })
        break
    }
  } else {
    sorted.sort((a, b) => {
      const diff = compareDates(getCreatedAt(a), getCreatedAt(b))
      return diff !== 0 ? diff * -1 : compareReportNumbers(a.reportNumber, b.reportNumber) * -1
    })
  }

  const start = options?.skip ?? 0
  const take = options?.take ?? sorted.length
  const paged = sorted.slice(start, start + take)
  const pageNumber = Math.floor(start / Math.max(take, 1)) + 1

  await delay(200)
  return {
    items: paged,
    totalCount: sorted.length,
    pageNumber,
    totalPages: Math.max(1, Math.ceil(sorted.length / Math.max(take, 1))),
  }
}

export const getPcgReport = async (options?: { reportId?: string; projectId?: number | string | null }): Promise<PcgReport> => {
  if (API_URL) {
    try {
      const apiUrl = getApiUrl()
      const reportId = options?.reportId
      const url = reportId ? `${apiUrl}/${reportId}` : `${apiUrl}/template`
      const params: Record<string, string> = {}
      if (!reportId && options?.projectId) {
        params.projectId = options.projectId.toString()
      }
      const { data } = await axios.get<PcgReport>(url, { params })
      return data
    } catch (error) {
      console.warn('Falling back to mock PCG report.', error)
    }
  }
  await delay(150)
  return mockPcgReport
}

export const getPcgReportVariations = async (reportId: string): Promise<VariationLineItem[]> => {
  if (API_URL) {
    try {
      const apiUrl = getApiUrl()
      const { data } = await axios.get<VariationLineItem[]>(`${apiUrl}/${reportId}/variations`)
      return data ?? []
    } catch (error) {
      console.warn('Falling back to mock PCG report variations.', error)
    }
  }

  await delay(150)
  return mockPcgReport.variations ?? []
}

export const buildPcgReportImageUrl = (reportId?: string | number, fileName?: string | null) => {
  if (!API_URL || !reportId || !fileName) {
    return undefined
  }
  const trimmedFileName = fileName.trim()
  if (!trimmedFileName) {
    return undefined
  }
  return `${API_URL}/${reportId}/images/${encodeURIComponent(trimmedFileName)}`
}

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })

export const getPcgReportImageDataUrl = async (reportId: string | number, fileName: string) => {
  const imageUrl = buildPcgReportImageUrl(reportId, fileName)
  if (!imageUrl) {
    throw new Error('Unable to resolve the uploaded image URL.')
  }

  const response = await axios.get<Blob>(imageUrl, { responseType: 'blob' })
  return blobToDataUrl(response.data)
}

export const uploadPcgReportImage = async (
  reportId: string | number,
  file: File
): Promise<PcgReportImageUploadResponse> => {
  const apiUrl = getApiUrl()
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await axios.post<PcgReportImageUploadResponse>(`${apiUrl}/${reportId}/images`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return data
}

export const savePcgReportDraft = async (payload: PcgReport): Promise<PcgReport> => {
  if (API_URL) {
    const apiUrl = getApiUrl()
    const { data } = await axios.post<PcgReport>(apiUrl, encodePcgReportHtmlPayload(payload))
    return data
  }
  await delay(300)
  return {
    ...payload,
    meta: {
      ...payload.meta,
      id: payload.meta.id || mockPcgReport.meta.id,
      status: 'draft',
      statusLabel: 'Draft',
      documentStatus: payload.documentStatus,
      lastUpdated: new Date().toISOString(),
    },
  }
}

export const createPcgReportRevision = async (reportId: string): Promise<PcgReport> => {
  if (API_URL) {
    const apiUrl = getApiUrl()
    const { data } = await axios.post<PcgReport>(`${apiUrl}/${reportId}/revision`)
    return data
  }

  await delay(300)
  return {
    ...mockPcgReport,
    meta: {
      ...mockPcgReport.meta,
      id: `${Date.now()}`,
      mode: 'edit',
      status: 'draft',
      statusLabel: 'Draft',
      documentStatus: 'draft',
      reportNumber: `${mockPcgReport.meta.reportNumber ?? 'PCG'}-Rev-1`,
      lastUpdated: new Date().toISOString(),
    },
    documentStatus: 'draft',
  }
}

export const unlockPcgReport = async (reportId: string): Promise<PcgReport> => {
  const apiUrl = getApiUrl()
  const { data } = await axios.post<PcgReport>(`${apiUrl}/${reportId}/unlock`)
  return data
}

export const deletePcgReport = async (reportId: string): Promise<void> => {
  const apiUrl = getApiUrl()
  await axios.delete(`${apiUrl}/${reportId}`)
}

export const submitPcgReport = async (payload: PcgReport): Promise<PcgReport> => {
  if (API_URL) {
    const apiUrl = getApiUrl()
    const { data } = await axios.post<PcgReport>(`${apiUrl}/submit`, encodePcgReportHtmlPayload(payload))
    return data
  }
  await delay(300)
  return {
    ...payload,
    meta: {
      ...payload.meta,
      status: 'final',
      statusLabel: 'Final',
      documentStatus: 'final',
      lastUpdated: new Date().toISOString(),
    },
    documentStatus: 'final',
  }
}

export const generatePcgReportFile = async (reportId: string) => {
  if (API_URL) {
    const apiUrl = getApiUrl()
    const { data } = await axios.post(`${apiUrl}/${reportId}/report`, undefined, { responseType: 'blob' })
    return data as Blob
  }
  await delay(400)
  return new Blob(['Mock PCG report PDF'], { type: 'application/pdf' })
}

export const buildPcgReportSignatureUrl = (reportId?: string, fileName?: string | null) =>
    API_URL && reportId && fileName ? `${API_URL}/${reportId}/signatures/${encodeURIComponent(fileName)}` : undefined

export const uploadPcgPreparedSignature = async (reportId: string, file: File): Promise<{ fileName: string }> => {
    const form = new FormData()
    form.append('file', file)
    const { data } = await axios.post<{ fileName: string }>(`${getApiUrl()}/${reportId}/signatures/preparedBy`, form,
        { headers: { 'Content-Type': 'multipart/form-data' } })
    return data
}

export const searchPcgUsers = async (term: string, roles: string[]): Promise<PcgUserLookupItem[]> => {
    if (!API_URL) return []
    const params = new URLSearchParams()
    if (term) params.append('term', term)
    roles.forEach((role) => params.append('roles', role))
    return (await axios.get<PcgUserLookupItem[]>(`${getApiUrl()}/users`, { params })).data
}

export const getPcgRoles = async (): Promise<RoleLookupItem[]> => {
    if (!API_URL) return []
    return (await axios.get<RoleLookupItem[]>(`${process.env.REACT_APP_API_URL}/Role/getallroles`)).data
}

export const getPcgUserSignature = async (): Promise<UserSignatureResponse | undefined> => {
    if (!API_URL) return undefined
    const response = await axios.get<UserSignatureResponse>(`${getApiUrl()}/user-signatures`)
    return response.status === 204 || !response.data?.fileName ? undefined : response.data
}

export const buildPcgUserSignatureUrl = (fileName?: string) =>
    API_URL && fileName ? `${API_URL}/user-signatures/current/${encodeURIComponent(fileName)}` : undefined

export const uploadPcgSignature = async (reportId: string, slot: SignatureSlot, file: File): Promise<{ fileName: string }> => {
    const form = new FormData()
    form.append('file', file)
    const { data } = await axios.post<{ fileName: string }>(`${getApiUrl()}/${reportId}/signatures/${slot}`, form,
        { headers: { 'Content-Type': 'multipart/form-data' } })
    return data
}

export const pcgStatusOptions = mockPcgReportStatuses
